import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';


const API_BASE = '/api';
const ENV_MARKER = '\n\n---\n[자동 환경정보]\n';

const readError = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

const splitNote = (text) => {
  const value = String(text || '');
  const index = value.indexOf(ENV_MARKER);
  if (index < 0) return { content: value, environment: '' };
  return {
    content: value.slice(0, index).trimEnd(),
    environment: value.slice(index + ENV_MARKER.length).trim(),
  };
};

const responseData = (res) => res?.data?.data || {};

const nowKst = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return { date: `${value.year}-${value.month}-${value.day}`, time: `${value.hour}:${value.minute}` };
};

export default function FarmDiaryFormPage() {
  const { diaryId } = useParams();
  const editing = Boolean(diaryId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initial = nowKst();

  const [farms, setFarms] = useState([]);
  const [farmId, setFarmId] = useState(searchParams.get('farm_id') || '');
  const [title, setTitle] = useState('');
  const [workDate, setWorkDate] = useState(initial.date);
  const [workTime, setWorkTime] = useState(initial.time);
  const [content, setContent] = useState('');
  const [environment, setEnvironment] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/farms`)
      .then((res) => {
        const list = res.data?.data || [];
        setFarms(list);
        if (!farmId && list.length) setFarmId(String(list[0].id));
      })
      .catch((e) => setError(readError(e, '농장 목록을 불러오지 못했습니다.')));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!farmId) return;
    axios.get(`${API_BASE}/farms/${farmId}/smartfarm-environment`)
      .then((res) => setEnvironment(res.data?.data?.environment || null))
      .catch(() => setEnvironment(null));
  }, [farmId]);

  useEffect(() => {
    if (!editing || !farmId) return;
    setLoading(true);
    axios.get(`${API_BASE}/farms/${farmId}/diary-notes/${diaryId}`)
      .then((res) => {
        const note = responseData(res).note || {};
        const dt = String(note.date_time || '');
        setTitle(note.name || '');
        setWorkDate(dt.slice(0, 10) || initial.date);
        setWorkTime(dt.slice(11, 16) || initial.time);
        setContent(splitNote(note.note).content);
      })
      .catch((e) => setError(readError(e, '영농일지를 불러오지 못했습니다.')))
      .finally(() => setLoading(false));
  }, [editing, farmId, diaryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadAttachments = async (uuid) => {
    if (!attachments.length) return;
    const formData = new FormData();
    attachments.forEach((file) => formData.append('files', file));
    await axios.post(`${API_BASE}/farms/${farmId}/notes/${uuid}/attachments`, formData);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!farmId) return setError('농장을 선택하세요.');
    if (!title.trim()) return setError('제목을 입력하세요.');

    setSaving(true);
    setError('');

    try {
      const payload = {
        name: title.trim(),
        date_time: `${workDate}T${workTime || '00:00'}:00+09:00`,
        note: content,
      };

      if (editing) {
        await axios.put(`${API_BASE}/farms/${farmId}/diary-notes/${diaryId}`, payload);
        await uploadAttachments(diaryId);
        navigate(`/farm-diary/detail/${diaryId}?farm_id=${farmId}`);
      } else {
        const res = await axios.post(`${API_BASE}/farms/${farmId}/diary-notes`, payload);

        // 오늘 실증한 create 응답: data.note.unique_id
        const uuid = res.data?.data?.note?.unique_id;
        if (!uuid) throw new Error('Mycodo Note UUID를 받지 못했습니다.');

        await uploadAttachments(uuid);
        navigate(`/farm-diary/detail/${uuid}?farm_id=${farmId}`);
      }
    } catch (e2) {
      setError(readError(e2, '저장하지 못했습니다.'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-5 text-center"><div className="spinner-border text-success" /></div>;

  return (
    <div className="container-fluid py-4 farm-diary-page" style={{ maxWidth: 1100 }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-1">{editing ? '영농일지 수정' : '영농일지 작성'}</h2>
          <div className="text-secondary">작업 내용과 환경정보를 함께 기록합니다.</div>
        </div>
        <Link className="btn btn-outline-secondary" to={editing ? `/farm-diary/detail/${diaryId}?farm_id=${farmId}` : `/farm-diary/list?farm_id=${farmId}`}>취소</Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={submit}>
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3"><strong>기본 정보</strong></div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">농장 *</label>
                <select required disabled={editing} className="form-select" value={farmId} onChange={(e) => setFarmId(e.target.value)}>
                  <option value="">선택</option>
                  {farms.map((farm) => <option key={farm.id} value={farm.id}>{farm.name}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">작업일 *</label>
                <input required type="date" className="form-control" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">시간 *</label>
                <input required type="time" className="form-control" value={workTime} onChange={(e) => setWorkTime(e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label">제목 *</label>
                <input required maxLength={200} className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label">내용</label>
                <textarea rows={7} className="form-control" value={content} onChange={(e) => setContent(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3"><strong>스마트팜 환경 정보</strong></div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4"><label className="form-label">온도</label><input className="form-control" readOnly value={environment?.temperature != null ? `${environment.temperature} ℃` : '-'} /></div>
              <div className="col-md-4"><label className="form-label">습도</label><input className="form-control" readOnly value={environment?.humidity != null ? `${environment.humidity} %` : '-'} /></div>
              <div className="col-md-4"><label className="form-label">강우량</label><input className="form-control" readOnly value={environment?.rainfall != null ? `${environment.rainfall} mm` : '-'} /></div>
            </div>
            <div className="form-text mt-2">저장할 때 Mycodo KMA의 현재 온도·습도·강우량이 자동 기록됩니다.</div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3"><strong>첨부파일</strong></div>
          <div className="card-body">
            <input type="file" className="form-control" multiple accept="image/*" onChange={(e) => setAttachments(Array.from(e.target.files || []))} />
            {attachments.length > 0 && <div className="small text-secondary mt-2">{attachments.length}개 이미지 선택됨</div>}
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <Link className="btn btn-outline-secondary" to={`/farm-diary/list?farm_id=${farmId}`}>취소</Link>
          <button className="btn btn-success px-4" disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </form>
    </div>
  );
}
